import React, { useMemo, useState } from "react";
import { auth } from "../firebase";
import { colors, radius, spacing, typography } from "../utils/designTokens";
import { useUser } from "../context/UserContext";
import { useToast } from "./Toast";
import { useMaterialesData } from "../hooks/useMaterialesData";
import { DEFAULT_PUNTOS, buildFlyerHtml } from "../utils/flyerDesarrolladoras";
import { descargarFlyerPdf, descargarFlyerImagen } from "../utils/flyerExport";
import { loadContacto } from "../screens/comercial/materialesUi";
import { plantillaWhatsapp, plantillaMail } from "../utils/plantillasDesarrolladoras";
import { FaWhatsapp, FaRegEnvelope, FaRegCopy, FaCheck, FaExternalLinkAlt, FaDownload, FaRegFileAlt } from "react-icons/fa";

const digitosWa = (tel) => {
  const d = String(tel || "").replace(/\D/g, "");
  if (!d) return "";
  return d.length <= 9 ? `598${d}` : d; // UY: sin código de país → agregar 598
};

/**
 * Panel de primer contacto con una desarrolladora: arma el mensaje (WhatsApp o
 * mail) con la plantilla + el nombre de la desarrolladora y del agente logueado,
 * para copiar/enviar, y descarga el folleto elegido (personalizado con el nombre
 * de la desarrolladora) para adjuntar.
 */
export default function ContactoDesarrolladora({ dev }) {
  const { showToast } = useToast();
  const { userName, userEmail } = useUser();
  const { materiales } = useMaterialesData();
  const email = (userEmail || auth.currentUser?.email || "").toLowerCase();
  const c0 = loadContacto(email);
  const agenteNombre = c0.name || userName || "el equipo de Habitia";
  const agente = { name: agenteNombre, email: c0.email || email, tel: c0.tel || "", cargo: "Agente Comercial" };

  const [canal, setCanal] = useState("wsp"); // wsp | mail
  const [copiado, setCopiado] = useState("");
  const [exportando, setExportando] = useState(false);

  const desarrolladora = (dev?.nombre || "").trim() || "el estudio";
  const vars = { desarrolladora, agente: agenteNombre };
  const wspText = useMemo(() => plantillaWhatsapp(vars), [desarrolladora, agenteNombre]); // eslint-disable-line
  const mail = useMemo(() => plantillaMail(vars, { tel: agente.tel, email: agente.email, cargo: agente.cargo }), [desarrolladora, agenteNombre, agente.tel, agente.email]); // eslint-disable-line

  // Folletos disponibles: el base + los flyers guardados en Materiales.
  const folletos = useMemo(() => [
    { id: "__base__", nombre: "Folleto desarrolladoras (base)", puntos: DEFAULT_PUNTOS },
    ...materiales.filter((m) => m.tipo === "flyer_desarrolladoras").map((m) => ({ id: m.id, nombre: m.nombre || "Folleto", puntos: m.puntos || DEFAULT_PUNTOS })),
  ], [materiales]);
  const [folletoId, setFolletoId] = useState("__base__");
  const folleto = folletos.find((f) => f.id === folletoId) || folletos[0];

  const telWa = digitosWa(dev?.contactoTel || dev?.telefono);
  const mailTo = (dev?.contactoEmail || dev?.email || "").trim();

  async function copiar(texto, cual) {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(cual);
      setTimeout(() => setCopiado((v) => (v === cual ? "" : v)), 2000);
    } catch { showToast("No se pudo copiar.", "error"); }
  }

  function abrirWhatsapp() {
    if (!telWa) { showToast("La desarrolladora no tiene teléfono cargado.", "error"); return; }
    window.open(`https://wa.me/${telWa}?text=${encodeURIComponent(wspText)}`, "_blank", "noopener");
  }
  function abrirMail() {
    if (!mailTo) { showToast("La desarrolladora no tiene email cargado.", "error"); return; }
    window.location.href = `mailto:${mailTo}?subject=${encodeURIComponent(mail.asunto)}&body=${encodeURIComponent(mail.cuerpo)}`;
  }

  const slug = () => desarrolladora.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "desarrolladora";
  async function descargarFolleto(tipo) {
    setExportando(true);
    try {
      const html = buildFlyerHtml({ destinatario: desarrolladora, puntos: folleto.puntos, agente });
      const fn = `folleto-habitia-${slug()}`;
      if (tipo === "pdf") await descargarFlyerPdf(html, `${fn}.pdf`);
      else if (tipo === "png") await descargarFlyerImagen(html, `${fn}.png`);
      else {
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${fn}.html`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      showToast("Folleto descargado — adjuntalo al mensaje.", "success");
    } catch { showToast("No se pudo generar el folleto.", "error"); }
    setExportando(false);
  }

  return (
    <div style={box}>
      {/* Canal */}
      <div style={{ display: "flex", gap: spacing[2], marginBottom: spacing[3] }}>
        <button type="button" onClick={() => setCanal("wsp")} style={tabBtn(canal === "wsp")}><FaWhatsapp size={13} /> WhatsApp</button>
        <button type="button" onClick={() => setCanal("mail")} style={tabBtn(canal === "mail")}><FaRegEnvelope size={13} /> Mail</button>
      </div>

      {/* Folleto */}
      <label htmlFor="dd-folleto" style={lbl}>Folleto a enviar</label>
      <select id="dd-folleto" value={folletoId} onChange={(e) => setFolletoId(e.target.value)} style={{ ...input, cursor: "pointer", marginBottom: spacing[3] }}>
        {folletos.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
      </select>

      {/* Mensaje */}
      {canal === "wsp" ? (
        <>
          <label style={lbl}>Mensaje de WhatsApp</label>
          <div style={preview}>{wspText}</div>
          <div style={acciones}>
            <button type="button" onClick={() => copiar(wspText, "wsp")} style={btnPri}>{copiado === "wsp" ? <FaCheck size={12} /> : <FaRegCopy size={12} />} {copiado === "wsp" ? "Copiado" : "Copiar mensaje"}</button>
            <button type="button" onClick={abrirWhatsapp} style={btnSec}><FaExternalLinkAlt size={11} /> Abrir WhatsApp{telWa ? "" : " (falta tel.)"}</button>
          </div>
        </>
      ) : (
        <>
          <label style={lbl}>Asunto</label>
          <div style={{ ...preview, marginBottom: spacing[2] }}>{mail.asunto}</div>
          <label style={lbl}>Cuerpo del mail</label>
          <div style={preview}>{mail.cuerpo}</div>
          <div style={acciones}>
            <button type="button" onClick={() => copiar(mail.asunto, "asunto")} style={btnSec}>{copiado === "asunto" ? <FaCheck size={12} /> : <FaRegCopy size={12} />} Asunto</button>
            <button type="button" onClick={() => copiar(mail.cuerpo, "cuerpo")} style={btnPri}>{copiado === "cuerpo" ? <FaCheck size={12} /> : <FaRegCopy size={12} />} {copiado === "cuerpo" ? "Copiado" : "Copiar cuerpo"}</button>
            <button type="button" onClick={abrirMail} style={btnSec}><FaExternalLinkAlt size={11} /> Abrir en el mail{mailTo ? "" : " (falta email)"}</button>
          </div>
        </>
      )}

      {/* Descargar folleto para adjuntar */}
      <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginTop: spacing[3], flexWrap: "wrap" }}>
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600, display: "inline-flex", alignItems: "center", gap: 5 }}><FaRegFileAlt size={11} /> Folleto para adjuntar:</span>
        <button type="button" onClick={() => descargarFolleto("pdf")} disabled={exportando} style={btnMini}><FaDownload size={10} /> PDF</button>
        <button type="button" onClick={() => descargarFolleto("png")} disabled={exportando} style={btnMini}><FaDownload size={10} /> PNG</button>
        <button type="button" onClick={() => descargarFolleto("html")} disabled={exportando} style={btnMini}><FaDownload size={10} /> HTML</button>
        {exportando && <span style={{ fontSize: typography.sizes.xs, color: colors.gray400 }}>Generando…</span>}
      </div>
      <p style={{ margin: `${spacing[2]}px 0 0`, fontSize: typography.sizes.xs, color: colors.gray400 }}>
        El folleto sale personalizado con “{desarrolladora}” y tu contacto. Copiá el mensaje, adjuntá el folleto y enviá.
      </p>
    </div>
  );
}

const box = { padding: spacing[3], background: colors.gray50, border: `1px solid ${colors.gray200}`, borderRadius: radius.md };
const lbl = { display: "block", fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: 4 };
const input = { width: "100%", padding: "9px 11px", boxSizing: "border-box", border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, fontSize: typography.sizes.base, fontFamily: typography.fontFamily, background: colors.white, color: colors.gray900 };
const preview = { whiteSpace: "pre-wrap", background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: spacing[3], fontSize: typography.sizes.sm, color: colors.gray700, lineHeight: 1.5 };
const acciones = { display: "flex", gap: spacing[2], marginTop: spacing[2], flexWrap: "wrap" };
const tabBtn = (active) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: `1px solid ${active ? colors.primary : colors.gray200}`, borderRadius: radius.sm, background: active ? colors.primaryLighter : colors.white, color: active ? colors.primary : colors.gray700, cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit" });
const btnPri = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "none", borderRadius: radius.sm, background: colors.primary, color: colors.white, cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit" };
const btnSec = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, background: colors.white, color: colors.gray700, cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit" };
const btnMini = { display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 10px", border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, background: colors.white, color: colors.gray700, cursor: "pointer", fontSize: typography.sizes.xs, fontFamily: "inherit" };
